import React, { useEffect, useState } from "react";
import axios from "axios";
import "./UserManagement.css";

// =====================================
// AVAILABLE INTERFACES (must match sidebar module names)
// =====================================
const ALL_INTERFACES = [
  "Food Committee",
  "Visitors",
  "Vehicles",
  "Policy Matters",
  "Transportation",
  "Agreements / Insurance",
  "Security",
  "CSR",
  "Cleaning & Repairs",
  "Accident Records",
  "Transport Committee",
];

const API_BASE = "https://hrpsc.swisstekaluminium.com/api/auth";

function UserManagement() {
  const myRole = localStorage.getItem("role");
  const token = localStorage.getItem("token");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Which user row is currently open for editing
  const [editingUserId, setEditingUserId] = useState(null);

  // Draft selection while editing (userId -> array of interfaces)
  const [draftInterfaces, setDraftInterfaces] = useState([]);

  const [savingId, setSavingId] = useState(null);

  // =====================================
  // ADD USER (pre-provision) form state - Admin & Super Admin
  // =====================================
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newFullName, setNewFullName] = useState("");
  const [newInterfaces, setNewInterfaces] = useState([]);
  const [addingUser, setAddingUser] = useState(false);

  // =====================================
  // ADD ADMIN form state - Super Admin only
  // =====================================
  const [showAddAdminForm, setShowAddAdminForm] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log("Fetch Users Error:", err);
      setError(
        err.response?.data?.message ||
          "Failed to load users. Please check your connection."
      );
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (user) => {
    setEditingUserId(user.id);
    setDraftInterfaces(user.assignedInterfaces || []);
    setSuccessMsg("");
    setError("");
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setDraftInterfaces([]);
  };

  const toggleInterface = (interfaceName) => {
    setDraftInterfaces((prev) =>
      prev.includes(interfaceName)
        ? prev.filter((i) => i !== interfaceName)
        : [...prev, interfaceName]
    );
  };

  const saveInterfaces = async (userId) => {
    setSavingId(userId);
    setError("");
    setSuccessMsg("");

    try {
      const res = await axios.put(
        `${API_BASE}/assign-interfaces/${userId}`,
        { assignedInterfaces: draftInterfaces },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local list without a full refetch
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, assignedInterfaces: res.data.assignedInterfaces }
            : u
        )
      );

      setSuccessMsg("Interfaces updated successfully.");
      setEditingUserId(null);
    } catch (err) {
      console.log("Assign Interfaces Error:", err);
      setError(
        err.response?.data?.message || "Failed to update interfaces."
      );
    } finally {
      setSavingId(null);
    }
  };

  // =====================================
  // ADD USER (pre-provision) handlers
  // =====================================

  const toggleNewInterface = (interfaceName) => {
    setNewInterfaces((prev) =>
      prev.includes(interfaceName)
        ? prev.filter((i) => i !== interfaceName)
        : [...prev, interfaceName]
    );
  };

  const resetAddForm = () => {
    setNewEmail("");
    setNewFullName("");
    setNewInterfaces([]);
    setShowAddForm(false);
  };

  const deleteUser = async (user) => {
    const confirmed = window.confirm(
      `Remove ${user.fullName || user.username}? This cannot be undone - they will lose access immediately and would need to be re-added.`
    );

    if (!confirmed) return;

    setError("");
    setSuccessMsg("");

    try {
      await axios.delete(`${API_BASE}/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      setSuccessMsg(`${user.fullName || user.username} removed successfully.`);
    } catch (err) {
      console.log("Delete User Error:", err);
      setError(err.response?.data?.message || "Failed to remove user.");
    }
  };

  const submitAddUser = async () => {
    if (!newEmail.trim()) {
      setError("Email is required to register a user.");
      return;
    }

    setAddingUser(true);
    setError("");
    setSuccessMsg("");

    try {
      const res = await axios.post(
        `${API_BASE}/register-user`,
        {
          username: newEmail.trim(),
          fullName: newFullName.trim(),
          assignedInterfaces: newInterfaces,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers((prev) => [...prev, res.data.user]);
      setSuccessMsg(
        "User registered. They'll have this access as soon as they log in with Microsoft."
      );
      resetAddForm();
    } catch (err) {
      console.log("Register User Error:", err);
      setError(
        err.response?.data?.message || "Failed to register user."
      );
    } finally {
      setAddingUser(false);
    }
  };

  // =====================================
  // ADD ADMIN handlers
  // =====================================

  const resetAddAdminForm = () => {
    setNewAdminUsername("");
    setNewAdminPassword("");
    setShowAddAdminForm(false);
  };

  const submitAddAdmin = async () => {
    if (!newAdminUsername.trim() || !newAdminPassword.trim()) {
      setError("Username and password are both required to create an admin.");
      return;
    }

    if (newAdminPassword.trim().length < 6) {
      setError("Password should be at least 6 characters.");
      return;
    }

    setAddingAdmin(true);
    setError("");
    setSuccessMsg("");

    try {
      // create-admin only returns a message, not the created user object,
      // so we refetch the list to bring the new admin in.
      await axios.post(
        `${API_BASE}/create-admin`,
        {
          username: newAdminUsername.trim(),
          password: newAdminPassword.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMsg("Admin account created successfully.");
      resetAddAdminForm();
      await fetchUsers();
    } catch (err) {
      console.log("Create Admin Error:", err);
      setError(err.response?.data?.message || "Failed to create admin.");
    } finally {
      setAddingAdmin(false);
    }
  };

  // Only admin / superadmin should ever see this page.
  // (Backend already enforces this on every route - this is just UI-level.)
  if (myRole !== "admin" && myRole !== "superadmin") {
    return (
      <div className="user-mgmt-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  const regularUsers = users.filter((u) => u.role === "user");
  const adminUsers = users.filter(
    (u) => u.role === "admin" || u.role === "superadmin"
  );

  return (
    <div>
      <div className="dashboard-header">
        <div>
          <h1>User Management</h1>
          <p>Assign interface access to users</p>
        </div>

        {(myRole === "admin" || myRole === "superadmin") && !showAddForm && !showAddAdminForm && (
          <div className="user-mgmt-header-actions">
            <button
              className="user-mgmt-btn primary"
              onClick={() => setShowAddForm(true)}
            >
              + Add User
            </button>

            {myRole === "superadmin" && (
              <button
                className="user-mgmt-btn secondary"
                onClick={() => setShowAddAdminForm(true)}
              >
                + Add Admin
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div className="user-mgmt-alert error">{error}</div>}
      {successMsg && (
        <div className="user-mgmt-alert success">{successMsg}</div>
      )}

      {/* ===================================== */}
      {/* ADD USER FORM - ADMIN & SUPER ADMIN */}
      {/* ===================================== */}

      {(myRole === "admin" || myRole === "superadmin") && showAddForm && (
        <div className="user-mgmt-card user-mgmt-add-card">
          <h3>Register New User</h3>
          <p className="user-mgmt-add-hint">
            Add an employee's company email now and assign their access
            in advance. Once they log in with Microsoft, they'll land
            with this access already set up.
          </p>

          <div className="user-mgmt-form-row">
            <label>Company Email</label>
            <input
              type="email"
              placeholder="employee@swisstek.lk"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </div>

          <div className="user-mgmt-form-row">
            <label>Full Name (optional)</label>
            <input
              type="text"
              placeholder="e.g. Nethmini Perera"
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
            />
          </div>

          <div className="user-mgmt-form-row">
            <label>Assign Interfaces</label>
            <div className="user-mgmt-checkbox-grid">
              {ALL_INTERFACES.map((interfaceName) => (
                <label className="user-mgmt-checkbox" key={interfaceName}>
                  <input
                    type="checkbox"
                    checked={newInterfaces.includes(interfaceName)}
                    onChange={() => toggleNewInterface(interfaceName)}
                  />
                  {interfaceName}
                </label>
              ))}
            </div>
          </div>

          <div className="user-mgmt-actions">
            <button
              className="user-mgmt-btn primary"
              disabled={addingUser}
              onClick={submitAddUser}
            >
              {addingUser ? "Registering..." : "Register User"}
            </button>

            <button
              className="user-mgmt-btn secondary"
              disabled={addingUser}
              onClick={resetAddForm}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ===================================== */}
      {/* ADD ADMIN FORM - SUPER ADMIN ONLY */}
      {/* ===================================== */}

      {myRole === "superadmin" && showAddAdminForm && (
        <div className="user-mgmt-card user-mgmt-add-card">
          <h3>Create New Admin</h3>
          <p className="user-mgmt-add-hint">
            Admin accounts sign in with a username and password (not
            Microsoft). They get full access to all interfaces
            immediately - no interface assignment needed.
          </p>

          <div className="user-mgmt-form-row">
            <label>Username</label>
            <input
              type="text"
              placeholder="e.g. admin_kasun"
              value={newAdminUsername}
              onChange={(e) => setNewAdminUsername(e.target.value)}
            />
          </div>

          <div className="user-mgmt-form-row">
            <label>Password</label>
            <input
              type="password"
              placeholder="At least 6 characters"
              value={newAdminPassword}
              onChange={(e) => setNewAdminPassword(e.target.value)}
            />
          </div>

          <div className="user-mgmt-actions">
            <button
              className="user-mgmt-btn primary"
              disabled={addingAdmin}
              onClick={submitAddAdmin}
            >
              {addingAdmin ? "Creating..." : "Create Admin"}
            </button>

            <button
              className="user-mgmt-btn secondary"
              disabled={addingAdmin}
              onClick={resetAddAdminForm}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <>
          <h2 className="dashboard-section-title">Users</h2>

          {regularUsers.length === 0 && (
            <p className="user-mgmt-empty">No users found.</p>
          )}

          <div className="user-mgmt-list">
            {regularUsers.map((user) => {
              const isEditing = editingUserId === user.id;

              return (
                <div className="user-mgmt-card" key={user.id}>
                  <div className="user-mgmt-card-top">
                    <div>
                      <h3>{user.fullName || user.username}</h3>
                      <span className="user-mgmt-email">
                        {user.username}
                      </span>
                    </div>

                    <span
                      className={`user-mgmt-status ${
                        user.isActive ? "active" : "inactive"
                      }`}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {!isEditing ? (
                    <>
                      <div className="user-mgmt-tags">
                        {user.assignedInterfaces &&
                        user.assignedInterfaces.length > 0 ? (
                          user.assignedInterfaces.map((i) => (
                            <span className="user-mgmt-tag" key={i}>
                              {i}
                            </span>
                          ))
                        ) : (
                          <span className="user-mgmt-tag empty">
                            No interfaces assigned
                          </span>
                        )}
                      </div>

                      <div className="user-mgmt-actions">
                        <button
                          className="user-mgmt-btn primary"
                          onClick={() => startEditing(user)}
                        >
                          Edit Access
                        </button>

                        {myRole === "superadmin" && (
                          <button
                            className="user-mgmt-btn danger"
                            onClick={() => deleteUser(user)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="user-mgmt-checkbox-grid">
                        {ALL_INTERFACES.map((interfaceName) => (
                          <label
                            className="user-mgmt-checkbox"
                            key={interfaceName}
                          >
                            <input
                              type="checkbox"
                              checked={draftInterfaces.includes(
                                interfaceName
                              )}
                              onChange={() =>
                                toggleInterface(interfaceName)
                              }
                            />
                            {interfaceName}
                          </label>
                        ))}
                      </div>

                      <div className="user-mgmt-actions">
                        <button
                          className="user-mgmt-btn primary"
                          disabled={savingId === user.id}
                          onClick={() => saveInterfaces(user.id)}
                        >
                          {savingId === user.id ? "Saving..." : "Save"}
                        </button>

                        <button
                          className="user-mgmt-btn secondary"
                          disabled={savingId === user.id}
                          onClick={cancelEditing}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <h2 className="dashboard-section-title">Admins</h2>

          <div className="user-mgmt-list">
            {adminUsers.map((user) => (
              <div className="user-mgmt-card" key={user.id}>
                <div className="user-mgmt-card-top">
                  <div>
                    <h3>{user.fullName || user.username}</h3>
                    <span className="user-mgmt-email">{user.username}</span>
                  </div>

                  <span
                    className={`user-mgmt-status ${
                      user.isActive ? "active" : "inactive"
                    }`}
                  >
                    {user.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <p className="user-mgmt-full-access">
                  {user.role === "superadmin"
                    ? "Super Admin - full access"
                    : "Admin - full access to all interfaces"}
                </p>

                {myRole === "superadmin" && user.role !== "superadmin" && (
                  <button
                    className="user-mgmt-btn danger"
                    onClick={() => deleteUser(user)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default UserManagement;